'use client';

import { useNotifications, type Notification } from '@/contexts/NotificationContext';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

function useNotificationLabels(): Record<string, string> {
  const { t } = useTranslation('common');
  return {
    'session.new': t('notifications.types.newSession'),
    'session.activated': t('notifications.types.sessionActivated'),
    'session.rejected': t('notifications.types.sessionRejected'),
    'session.completed': t('notifications.types.sessionCompleted'),
    'session.completion_confirmed': t('notifications.types.sessionCompletionConfirmed'),
    'session.terminated': t('notifications.types.sessionTerminated'),
    'session.message_received': t('notifications.types.newMessage'),
    'settlement.completed': t('notifications.types.settlement'),
    'agent.approved': t('notifications.types.agentApproved'),
    'agent.rejected': t('notifications.types.agentRejected'),
    'agent.suspended': t('notifications.types.agentSuspended'),
    'review.received': t('notifications.types.newReview'),
    'wallet.payment': t('notifications.types.payment'),
    'wallet.income': t('notifications.types.income'),
    'refund.requested': t('notifications.types.refundRequested'),
    'refund.approved': t('notifications.types.refundApproved'),
    'refund.rejected': t('notifications.types.refundRejected'),
    'refund.escalated': t('notifications.types.refundEscalated'),
    'refund.timeout_rejected': t('notifications.types.refundTimeoutRejected'),
    'withdrawal.failed': t('notifications.types.withdrawalFailed'),
    'system.funds_alert': t('notifications.types.fundsAlert'),
  };
}

function getTypeColor(type: string): string {
  if (type.startsWith('agent.approved') || type.startsWith('wallet.income'))
    return 'bg-success-bg text-success';
  if (type.startsWith('agent.rejected') || type.startsWith('agent.suspended') || type === 'withdrawal.failed' || type.startsWith('system.funds_alert'))
    return 'bg-error-bg text-error';
  if (type.startsWith('session.'))
    return 'bg-info-bg text-info';
  if (type.startsWith('review.'))
    return 'bg-accent-50 text-brand-600';
  if (type.startsWith('wallet.'))
    return 'bg-warning-bg text-warning';
  return 'bg-surface-2 text-foreground';
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function NotificationItem({
  notification,
  onRead,
  labels,
}: {
  notification: Notification;
  onRead: (id: string) => void;
  labels: Record<string, string>;
}) {
  return (
    <div
      className={`px-4 py-3 border-b border-border cursor-pointer transition-colors hover:bg-surface-1 ${
        notification.isRead ? 'opacity-60' : ''
      }`}
      onClick={() => {
        if (!notification.isRead) onRead(notification.id);
      }}
    >
      <div className="flex items-start gap-2">
        <span
          className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${getTypeColor(notification.type)}`}
        >
          {labels[notification.type] ?? notification.type}
        </span>
        {!notification.isRead && (
          <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-info" />
        )}
      </div>
      <p className="mt-1 text-sm font-medium text-foreground">{notification.title}</p>
      {notification.body && (
        <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{notification.body}</p>
      )}
      <p className="mt-1 text-[11px] text-muted-foreground">{timeAgo(notification.createdAt)}</p>
    </div>
  );
}

export function NotificationPanel() {
  const { t } = useTranslation('common');
  const {
    notifications,
    isPanelOpen,
    closePanel,
    markAsRead,
    markAllAsRead,
    loadMore,
    hasMore,
    isLoading,
    unreadCount,
  } = useNotifications();

  const panelRef = useRef<HTMLDivElement>(null);
  const labels = useNotificationLabels();

  // Close panel on click outside
  useEffect(() => {
    if (!isPanelOpen) return;

    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        closePanel();
      }
    }

    // Delay to avoid the toggle click being caught
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPanelOpen, closePanel]);

  if (!isPanelOpen) return null;

  return (
    <div
      ref={panelRef}
      className="absolute right-0 top-full mt-2 w-80 max-h-[480px] rounded-lg border border-border bg-card shadow-lg z-50 flex flex-col overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">{t('notifications.title')}</h3>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className="text-xs text-brand-600 hover:text-brand-700 font-medium"
          >
            {t('notifications.markAllAsRead')}
          </button>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-8 w-8 mb-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
              />
            </svg>
            <p className="text-sm">{t('notifications.noNotifications')}</p>
          </div>
        ) : (
          <>
            {notifications.map((n) => (
              <NotificationItem key={n.id} notification={n} onRead={markAsRead} labels={labels} />
            ))}
            {hasMore && (
              <button
                onClick={loadMore}
                disabled={isLoading}
                className="w-full py-2.5 text-xs text-muted-foreground hover:text-foreground hover:bg-surface-1 transition-colors disabled:opacity-50"
              >
                {isLoading ? t('actions.loading') : t('notifications.loadMore')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
